import React, { useRef, useCallback } from 'react';
import { Card, Typography, Box } from '@mui/material';
import { GoogleMap, LoadScript, Marker, Circle } from '@react-google-maps/api';

const ScanDensityMap = ({ totalScans = [] }) => {
    const mapRef = useRef(null);
    const scanPoints = totalScans.filter(scan => scan.latitude && scan.longitude);

    const onMapLoad = useCallback((map) => {
        mapRef.current = map;

        if (scanPoints.length > 1) {
            const bounds = new window.google.maps.LatLngBounds();
            scanPoints.forEach(scan => {
                bounds.extend({ lat: scan.latitude, lng: scan.longitude });
            });
            map.fitBounds(bounds, 100);

            window.google.maps.event.addListenerOnce(map, 'bounds_changed', () => {
                if (map.getZoom() > 12) map.setZoom(12); // prevent over-zoom
            });
        } else if (scanPoints.length === 1) {
            map.setCenter({ lat: scanPoints[0].latitude, lng: scanPoints[0].longitude });
            map.setZoom(12);
        }
    }, [scanPoints]);

    const handleZoomTo = (lat, lng, zoomLevel) => {
        if (mapRef.current) {
            mapRef.current.panTo({ lat, lng });
            mapRef.current.setZoom(zoomLevel);
        }
    };

    const initialCenter = scanPoints.length > 0
        ? { lat: scanPoints[0].latitude, lng: scanPoints[0].longitude }
        : { lat: 37.0902, lng: -95.7129 }; // Fallback: center of US

    const initialZoom = scanPoints.length > 0 ? 10 : 4;

    return (
        <Card sx={{ backgroundColor: '#202020', color: '#fff', p: 3, borderRadius: '12px' }}>
            <Typography variant="h5" sx={{ color: '#12c099', mb: 1 }}>
                Scan Locations
            </Typography>

            <Typography variant="body2" sx={{ color: '#ccc', mb: 2 }}>
                Only scans with location data (latitude & longitude) appear on this map.
                Circles represent approximate scan regions; markers show specific scan points.
            </Typography>

            {scanPoints.length === 0 ? (
                <Typography variant="body1" sx={{ color: '#888' }}>
                    No scan locations available to display.
                </Typography>
            ) : (
                <Box sx={{ height: '400px', borderRadius: '8px', overflow: 'hidden' }}>
                    <LoadScript googleMapsApiKey={process.env.REACT_APP_GOOGLE_API_KEY}>
                        <GoogleMap
                            onLoad={onMapLoad}
                            mapContainerStyle={{ width: '100%', height: '100%' }}
                            center={initialCenter}
                            zoom={initialZoom}
                            options={{
                                gestureHandling: 'greedy',
                                fullscreenControl: false,
                                streetViewControl: false,
                                mapTypeControl: false,
                            }}
                        >
                            {scanPoints.map((scan, index) => {
                                const lat = scan.latitude;
                                const lng = scan.longitude;
                                return (
                                    <React.Fragment key={index}>
                                        <Circle
                                            center={{ lat, lng }}
                                            radius={1500}
                                            options={{
                                                strokeColor: '#12c099',
                                                strokeOpacity: 0.5,
                                                strokeWeight: 1,
                                                fillColor: '#12c099',
                                                fillOpacity: 0.15,
                                                clickable: true,
                                            }}
                                            onClick={() => handleZoomTo(lat, lng, 13)}
                                        />
                                        <Marker
                                            position={{ lat, lng }}
                                            onClick={() => handleZoomTo(lat, lng, 16)}
                                        />

                                    </React.Fragment>
                                );
                            })}
                        </GoogleMap>
                    </LoadScript>
                </Box>
            )}
        </Card>
    );
};

export default ScanDensityMap;
